import { useEffect, useRef } from 'react'
import { Route, Routes, useLocation } from 'react-router'
import type { SedeConfig } from '../../sedi/types'
import { LocaleProvider, getCopy } from './content'
import { ROUTES, localeFromPath, matchRoute, navigationScroll, type RouteDef } from './routes'
import { applyHeadClient, applyNotFoundHeadClient, headMetaFor } from './head'
import { SedeProvider } from './sede'
import { SiteFrame } from './components/layout/SiteFrame'
import { PageMotion } from './motion/PageMotion'
import HomePage from './pages/HomePage'
import ServicePage from './pages/ServicePage'
import ZonePage from './pages/ZonePage'
import ContactPage from './pages/ContactPage'
import NotFoundPage from './pages/NotFoundPage'
import { trackPageview } from '../../analytics/collector'

/**
 * The routed site. Mounted under BrowserRouter in the browser
 * (VerdeVercelli2App) and under StaticRouter by the prerender, so it must
 * not assume a window at render time — everything browser-specific lives
 * in the effect below, which never runs on the server.
 *
 * Both locales are declared as real routes off the same table, so the SPA
 * and the prerendered files agree on every URL by construction. The table's
 * `static` routes (privacy, cookie, data deletion, credits) are the shared
 * legal section's documents and are not registered here.
 */

function elementFor(r: RouteDef) {
  switch (r.kind) {
    case 'home':
      return <HomePage />
    case 'service':
      return <ServicePage serviceKey={r.serviceKey!} />
    case 'zone':
      return <ZonePage />
    case 'contact':
      return <ContactPage />
    // Static pages of the shared legal section (src/legal/): never
    // registered below, so the router never reaches these.
    case 'legal':
    case 'credits':
      return null
  }
}

export function SiteRoutes({ sede }: { sede: SedeConfig }) {
  const location = useLocation()
  const locale = localeFromPath(location.pathname)

  // The pathname the previous effect run saw; null until the first run.
  const prevPath = useRef<string | null>(null)

  // Whether the page layer fades in: only on a real client navigation. The
  // ref is READ here, never written (the effect below writes it), so render
  // stays deterministic — null on the server and on the first client render
  // (the prerendered page and its hydration never animate), and still the
  // previous pathname while rendering a new one. StrictMode's double render
  // reads the same value twice.
  const animateIn = prevPath.current !== null && prevPath.current !== location.pathname

  // Client-side head sync after navigation (crawlers read the prerendered
  // head and never get here), plus the two things a real page load does for
  // free: scroll to the top and move focus to the new <h1>. Keyed on
  // location.key, not pathname+hash: re-clicking a `/#numeri` link after
  // scrolling away is a new navigation to an identical URL (new key, same
  // strings) and must scroll again. navigationScroll still returns 'none' for
  // a same-path, no-hash re-click, so the brand link stays inert.
  useEffect(() => {
    // Recorded before the route check, so a first load on an unknown path
    // still counts as "seen" and the next client navigation gets its focus.
    const prev = prevPath.current
    prevPath.current = location.pathname
    if (prev !== null && prev !== location.pathname) trackPageview(location.pathname)
    const m = matchRoute(location.pathname)
    if (!m) {
      // The client 404: drop the previous page's canonical/hreflang, noindex it.
      const notFoundLocale = localeFromPath(location.pathname)
      applyNotFoundHeadClient({ locale: notFoundLocale, copy: getCopy(notFoundLocale) })
      return
    }
    // Unconditional, including on mount: it corrects the SPA shell's head.
    applyHeadClient(headMetaFor(m.route, m.locale, getCopy(m.locale)))
    // On the initial load the browser already did the right thing, and
    // doing it again would undo it: scrolling to the top breaks `#fragment`
    // deep links and scroll restoration on reload/back, and focusing the
    // <h1> puts focus inside <main>, past the skip link and the header nav.
    // navigationScroll (routes.ts) returns 'none' for the first run, and for
    // StrictMode's dev-only second mount run when the URL has no hash (with
    // one, that run re-scrolls to the fragment the browser already showed —
    // harmless). A hash (a header link to `/#numeri`, on the same page or from
    // another one) scrolls to its fragment; a new path without one goes to
    // the top and moves focus to the new <h1>.
    const target = navigationScroll(prev, location.pathname, location.hash)
    if (target === 'none') return
    if (target === 'fragment') {
      // A malformed escape (`#%E0`) makes decodeURIComponent throw URIError,
      // which would escape the effect and unmount the root.
      let id = location.hash.slice(1)
      try { id = decodeURIComponent(id) } catch { /* keep the raw hash */ }
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' })
        return
      }
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    document.getElementById('main')?.querySelector<HTMLElement>('h1')?.focus?.()
  }, [location.key])

  // The sede reaches the chrome (Footer) and the pages through `useSede()`.
  // PageMotion is keyed by pathname: each navigation reverts the old page's
  // tweens and ScrollTriggers and builds the new page's from scratch.
  return (
    <SedeProvider sede={sede}>
      <LocaleProvider locale={locale}>
        <div className="vc2-root min-h-screen">
          <SiteFrame>
            <PageMotion key={location.pathname} animateIn={animateIn}>
              <Routes>
                {ROUTES.filter((r) => !r.static).flatMap((r) => [
                  <Route key={`${r.key}-it`} path={r.paths.it} element={elementFor(r)} />,
                  <Route key={`${r.key}-en`} path={r.paths.en} element={elementFor(r)} />,
                ])}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </PageMotion>
          </SiteFrame>
        </div>
      </LocaleProvider>
    </SedeProvider>
  )
}
